import React, { useMemo, useCallback } from 'react';
import { useShallowEqualSelector } from '../../../../../hooks/redux';

import { ReduxState } from '../../../../../redux/reducers';
import {
  StepItems,
  BookStepSettingPair,
} from '../../../../../molecules/merchant/resources/book/BooksStep';
import { SettingsModelTypeEvent } from '../../../../../interfaces/merchants/resources/books';
import { getMerchantResourcesBooks } from '../../../../../redux/selectors/merchants/resources/books';

import {
  goTo,
  nextStep,
  finalStep,
} from '../../../../../redux/actions/merchants/resources/books';
import BookingWithPageDetail from '../BookingWithPageDetail';

import ReservationFlowAuthPage from '../ReservationFlowAuthPage';
import { InputPaymentOrganism } from '../InputPayment';
import InputBookingEventDateTime from '../InputBookingEventDateTime';
import InputCompletionEvent from '../InputCompletionEvent';
import InputCompletionFailed from '../InputCompletionFailed';
import InputConfirmationEvent from '../InputConfirmationEvent';
import InputUserInfo from '../InputUserInfo';
import { BooksStepOrganism } from '../../../../reservation-flow/BooksStep';
import { useAppDispatch } from '../../../../../redux';

interface Props {
  isExpressFlow: boolean;
  merchantId: string;
  resourcePublicId: string;
  lineLiffId: string | undefined;
}

const FULL_WORKFLOW_STEPS = [
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];

const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];
const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

const ModelTypeEvent: React.FC<Props> = ({
  isExpressFlow,
  merchantId,
  resourcePublicId,
  lineLiffId,
}) => {
  const dispatch = useAppDispatch();

  const merchantResourcesBooks = useShallowEqualSelector((state: ReduxState) =>
    getMerchantResourcesBooks(state),
  );
  const { merchantResource, step, postable, renew } = merchantResourcesBooks;

  const settings = useMemo<SettingsModelTypeEvent>(
    () => merchantResourcesBooks.settings as SettingsModelTypeEvent,
    [merchantResourcesBooks],
  );
  const publicId = useMemo(
    () => merchantResourcesBooks.merchantId!,
    [merchantResourcesBooks],
  );

  const renderRequireLoginAt = useCallback(
    (step: number) => {
      return (
        <ReservationFlowAuthPage
          moveToNext={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          renew={renew}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [dispatch, renew, resourcePublicId],
  );

  const renderInputBookingEvent = useCallback(
    (step: number) => {
      if (isExpressFlow) {
        return (
          <BookingWithPageDetail
            merchantId={merchantId}
            merchantResource={merchantResource!}
            showPerformableStaffMembers={true}
          >
            <InputBookingEventDateTime
              modelType={settings.modelType}
              settings={settings.event}
              postable={postable!}
              renew={renew}
              onSubmit={() => dispatch(nextStep(step))}
              onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
              withExpressFlow={isExpressFlow}
              merchantPublicId={merchantId}
              resourcePublicId={resourcePublicId}
            />
          </BookingWithPageDetail>
        );
      } else {
        return (
          <InputBookingEventDateTime
            modelType={settings.modelType}
            settings={settings.event}
            postable={postable!}
            renew={renew}
            onSubmit={() => dispatch(nextStep(step))}
            onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
            withExpressFlow={isExpressFlow}
            merchantPublicId={merchantId}
            resourcePublicId={resourcePublicId}
          />
        );
      }
    },
    [
      dispatch,
      isExpressFlow,
      merchantId,
      merchantResource,
      postable,
      renew,
      resourcePublicId,
      settings,
    ],
  );

  const renderInputUserInfoAt = useCallback(
    (step: number, submitLabelIntlId: string) => {
      return (
        <InputUserInfo
          settings={settings.userInfo}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          submitLabelIntlId={submitLabelIntlId}
          merchantPublicId={merchantId}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [dispatch, settings, merchantId, resourcePublicId],
  );

  const renderInputPaymentAt = useCallback(
    (step: number) => {
      return (
        <InputPaymentOrganism
          settings={settings.payment!}
          postable={postable!}
          merchantPublicId={publicId}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
        />
      );
    },
    [dispatch, settings, postable, publicId],
  );

  const renderInputConfirmationAt = useCallback(
    (step: number) => {
      return (
        <InputConfirmationEvent
          merchantResource={{
            publicId: merchantResource!.publicId,
            name: merchantResource!.name,
            imageUrl: merchantResource!.primaryImage?.thumb?.url,
          }}
          settings={settings.confirmation}
          postable={postable!}
          onSubmit={() => dispatch(finalStep(step))}
          onReinputPayment={() => dispatch(goTo(ERROR_REINPUT_PAYMENT_STEP))}
          onReselect={() => dispatch(goTo(ERROR_RESELECT_STEP))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          publicId={publicId}
          lineLiffId={lineLiffId}
        />
      );
    },
    [dispatch, settings, merchantResource, postable, publicId, lineLiffId],
  );

  const renderInputCompletion = useCallback(() => {
    return (
      <InputCompletionEvent
        merchantResource={{
          publicId: merchantResource!.publicId,
          name: merchantResource!.name,
          imageUrl: merchantResource!.primaryImage?.thumb?.url,
        }}
        settings={settings.completion!}
        publicId={publicId}
        withExpressFlow={isExpressFlow}
        resourcePublicId={resourcePublicId}
      />
    );
  }, [isExpressFlow, merchantResource, publicId, resourcePublicId, settings]);

  const renderFailedRestart = useCallback(() => {
    return (
      <InputCompletionFailed
        settings={settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload()}
      />
    );
  }, [settings]);

  const renderFailedReselect = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reselect'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [dispatch, settings],
  );

  const renderFailedReinput = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reinput'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [dispatch, settings],
  );

  const fullWorkFlowSetting = useCallback((): BookStepSettingPair => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingEvent(1) },
      2: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(2, 'books.submitLabel.payment') },
      3: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderInputPaymentAt(3)         },
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(4)    },
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletion()         },

      [ERROR_REINPUT_PAYMENT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReinput(3) },
      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [FULL_WORKFLOW_STEPS, setting];
  }, [
    renderFailedReinput,
    renderFailedReselect,
    renderFailedRestart,
    renderInputBookingEvent,
    renderInputCompletion,
    renderInputConfirmationAt,
    renderInputPaymentAt,
    renderInputUserInfoAt,
    renderRequireLoginAt,
    step,
  ]);

  const skipPaymentFlowSetting = useCallback((): BookStepSettingPair => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingEvent(1) },
      2: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(2, 'books.submitLabel.confirm') },
      3: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(3)    },
      4: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletion()         },

      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }, [
    renderFailedReselect,
    renderFailedRestart,
    renderInputBookingEvent,
    renderInputCompletion,
    renderInputConfirmationAt,
    renderInputUserInfoAt,
    renderRequireLoginAt,
    step,
  ]);

  const [allSteps, setting] = merchantResource!.paymentStep
    ? fullWorkFlowSetting()
    : skipPaymentFlowSetting();
  return (
    <>
      {setting && setting.currentProgress && setting.displayProgress && (
        <BooksStepOrganism
          allSteps={allSteps}
          current={allSteps.indexOf(setting.currentProgress) + 1}
          merchantPublicId={merchantId}
          resourcePublicId={merchantResource?.publicId ?? ''}
        />
      )}
      {setting && setting.renderFn()}
    </>
  );
};

export default ModelTypeEvent;
